// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\dashboard.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Context } from "../store/appContext";
import "../styles/dashboard.css";

const currencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const axisCurrencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const monthNames = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

const parseChartAmount = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined || value === "") return 0;
  const rawValue = String(value).trim().replace(/\s/g, "").replace(/€/g, "");
  const commaIndex = rawValue.lastIndexOf(",");
  const dotIndex = rawValue.lastIndexOf(".");
  const normalizedValue =
    commaIndex > dotIndex
      ? rawValue.replace(/\./g, "").replace(",", ".")
      : rawValue.replace(/,/g, "");
  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
const formatAxisCurrency = (value) => {
  const amount = parseChartAmount(value);
  return Math.abs(amount) > 0 && Math.abs(amount) < 1000
    ? currencyFormatter.format(amount)
    : axisCurrencyFormatter.format(amount);
};
const formatInteger = (value) => numberFormatter.format(Number(value || 0));
const formatOptionalInteger = (value) =>
  value === null || value === undefined ? "-" : formatInteger(value);
const formatOptionalCurrency = (value) =>
  value === null || value === undefined ? "-" : formatCurrency(value);

const formatLatestDataPeriod = (year, month) => {
  if (
    year === null ||
    year === undefined ||
    month === null ||
    month === undefined ||
    month < 1 ||
    month > 12
  )
    return "Último mes con datos: no disponible";
  return `Último mes con datos: ${monthNames[month - 1]} ${year}`;
};

const getSupplierCount = (row) =>
  row?.supplier_count ?? row?.distinct_suppliers;
const getPartDescription = (row) =>
  row?.description || row?.material_description || "-";
const getAverageUnitPrice = (row) =>
  row?.average_unit_price ?? row?.avg_unit_price ?? row?.average_price ?? null;

const getMonthlyTrend = (sortedHistory, valueGetter) => {
  if (sortedHistory.length < 2)
    return { label: "Sin dato anterior", direction: "neutral" };
  const current = Number(valueGetter(sortedHistory[sortedHistory.length - 1]));
  const previous = Number(valueGetter(sortedHistory[sortedHistory.length - 2]));
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0)
    return { label: "Sin dato anterior", direction: "neutral" };
  const delta = ((current - previous) / Math.abs(previous)) * 100;
  const sign = delta > 0 ? "+" : "";
  const direction = delta >= 0 ? "up" : "down";
  return { label: `${sign}${delta.toFixed(1)}% vs mes anterior`, direction };
};

const getMonthlyTrendForKey = (sortedHistory, key) =>
  getMonthlyTrend(sortedHistory, (item) => item?.[key]);

const getOptionalMonthlyTrendForKey = (sortedHistory, key) => {
  const latest = sortedHistory[sortedHistory.length - 1];
  const previous = sortedHistory[sortedHistory.length - 2];
  if (!latest || !previous || !(key in latest) || !(key in previous))
    return null;
  return getMonthlyTrendForKey(sortedHistory, key);
};

const buildPeriodKey = (year, month) =>
  `${year}-${String(month).padStart(2, "0")}`;

const getShiftedPeriod = (year, month, offset) => {
  const date = new Date(year, month - 1 + offset, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
};

const formatPeriodName = (year, month) => {
  if (!year || !month || month < 1 || month > 12)
    return "Periodo no disponible";
  return `${monthNames[month - 1]} ${year}`;
};

const getPeriodEntries = (item) => parseChartAmount(item?.entries_value);
const getPeriodExits = (item) => parseChartAmount(item?.exits_value);
const getPeriodBalance = (item) =>
  getPeriodEntries(item) - getPeriodExits(item);

const getBalanceTrend = (currentBalance, previousBalance, comparisonLabel) => {
  if (
    !Number.isFinite(currentBalance) ||
    !Number.isFinite(previousBalance) ||
    previousBalance === 0
  )
    return { label: "Sin dato anterior", direction: "neutral" };
  const delta =
    ((currentBalance - previousBalance) / Math.abs(previousBalance)) * 100;
  const sign = delta > 0 ? "+" : "";
  return {
    label: `${sign}${delta.toFixed(1)}% vs ${comparisonLabel}`,
    direction: delta >= 0 ? "up" : "down",
  };
};

const DashboardTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="tooltip-pro">
      <div className="tooltip-title">Periodo: {label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="tooltip-row">
          <span className="tooltip-color" style={{ background: entry.color }} />
          <span className="tooltip-label">
            {entry.dataKey === "entries" ? "Entradas" : "Salidas"}
          </span>
          <span className="tooltip-value">{formatCurrency(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const Dashboard = () => {
  const { store, actions } = useContext(Context);
  const navigate = useNavigate();
  const [period, setPeriod] = useState("12m");

  useEffect(() => {
    if (store.isAuthenticated) {
      if (
        !Array.isArray(store.suppliersCatalog) ||
        store.suppliersCatalog.length === 0
      )
        actions.getSuppliersCatalog();
      if (!Array.isArray(store.partsCatalog) || store.partsCatalog.length === 0)
        actions.getPartsCatalog();
    }
  }, [store.isAuthenticated]);

  const history = Array.isArray(store.inventoryHistory)
    ? store.inventoryHistory
    : [];
  const suppliers = Array.isArray(store.suppliersCatalog)
    ? store.suppliersCatalog
    : [];
  const parts = Array.isArray(store.partsCatalog) ? store.partsCatalog : [];
  const isAdmin = store.role === "admin";

  const sortedHistory = useMemo(
    () =>
      [...history].sort(
        (a, b) => a.year * 100 + a.month - (b.year * 100 + b.month),
      ),
    [history],
  );

  const visibleHistory = useMemo(() => {
    if (period === "6m") return sortedHistory.slice(-6);
    return sortedHistory.slice(-12);
  }, [period, sortedHistory]);

  const chartData = visibleHistory.map((item) => ({
    month: new Date(item.year, item.month - 1).toLocaleDateString("es-ES", {
      month: "short",
      year: "2-digit",
    }),
    entries: parseChartAmount(item.entries_value),
    exits: parseChartAmount(item.exits_value),
  }));

  const supplierKpis = suppliers.reduce(
    (accumulator, row) => ({
      totalSuppliers: accumulator.totalSuppliers + 1,
      totalSpend: accumulator.totalSpend + Number(row.total_spend || 0),
      purchaseLines:
        accumulator.purchaseLines + Number(row.purchase_lines || 0),
      distinctParts:
        accumulator.distinctParts + Number(row.distinct_parts || 0),
    }),
    { totalSuppliers: 0, totalSpend: 0, purchaseLines: 0, distinctParts: 0 },
  );

  const partKpis = parts.reduce(
    (accumulator, row) => ({
      totalParts: accumulator.totalParts + 1,
      totalSpend: accumulator.totalSpend + Number(row.total_spend || 0),
      purchaseLines:
        accumulator.purchaseLines + Number(row.purchase_lines || 0),
      supplierCount:
        accumulator.supplierCount + Number(getSupplierCount(row) || 0),
    }),
    { totalParts: 0, totalSpend: 0, purchaseLines: 0, supplierCount: 0 },
  );

  const latestPeriod = sortedHistory[sortedHistory.length - 1];
  const topSupplier = suppliers[0] || null;
  const topSuppliers = suppliers.slice(0, 3);
  const topParts = parts.slice(0, 3);
  const purchaseLinesTrend =
    getOptionalMonthlyTrendForKey(sortedHistory, "purchase_lines") ||
    getOptionalMonthlyTrendForKey(sortedHistory, "purchase_lines_count");
  const accumulatedPartsTrend =
    getOptionalMonthlyTrendForKey(sortedHistory, "distinct_parts") ||
    getOptionalMonthlyTrendForKey(sortedHistory, "parts_count") ||
    getOptionalMonthlyTrendForKey(sortedHistory, "total_parts");

  const openSupplier = (supplierId) => {
    if (supplierId) navigate(`/suppliers/${encodeURIComponent(supplierId)}`);
  };
  const openPart = (materialId) => {
    if (materialId) navigate(`/parts/${encodeURIComponent(materialId)}`);
  };
  const handleExport = async () => {
    await actions.exportDashboardInventoryReport();
  };

  const periodKpis = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const previousCalendarMonth = getShiftedPeriod(
      currentYear,
      currentMonth,
      -1,
    );
    const historyByPeriod = new Map(
      sortedHistory.map((item) => [
        buildPeriodKey(item.year, item.month),
        item,
      ]),
    );
    const findHistoryPeriod = (year, month) =>
      historyByPeriod.get(buildPeriodKey(year, month));
    const buildMonthlyKpi = ({
      id,
      icon,
      title,
      year,
      month,
      emptyMessage,
    }) => {
      const item = findHistoryPeriod(year, month);
      const previousPeriod = getShiftedPeriod(year, month, -1);
      const previousItem = findHistoryPeriod(
        previousPeriod.year,
        previousPeriod.month,
      );
      const hasData = Boolean(item);
      const balance = hasData ? getPeriodBalance(item) : 0;
      const previousBalance = previousItem
        ? getPeriodBalance(previousItem)
        : null;
      return {
        id,
        icon,
        title,
        periodLabel: formatPeriodName(year, month),
        entriesLabel: "Entradas del mes",
        exitsLabel: "Salidas del mes",
        balanceLabel: "Balance del mes",
        entries: hasData ? getPeriodEntries(item) : null,
        exits: hasData ? getPeriodExits(item) : null,
        balance: hasData ? balance : null,
        status: hasData ? null : emptyMessage,
        trend: hasData
          ? getBalanceTrend(balance, previousBalance, "mes anterior")
          : { label: "Sin comparativa", direction: "neutral" },
      };
    };

    const currentYearHistory = sortedHistory.filter(
      (item) => item.year === currentYear,
    );
    const currentYearEntries = currentYearHistory.reduce(
      (acc, item) => acc + getPeriodEntries(item),
      0,
    );
    const currentYearExits = currentYearHistory.reduce(
      (acc, item) => acc + getPeriodExits(item),
      0,
    );
    const currentYearBalance = currentYearEntries - currentYearExits;
    const previousYearHistory = sortedHistory.filter(
      (item) => item.year === currentYear - 1,
    );
    const previousYearEntries = previousYearHistory.reduce(
      (acc, item) => acc + getPeriodEntries(item),
      0,
    );
    const previousYearExits = previousYearHistory.reduce(
      (acc, item) => acc + getPeriodExits(item),
      0,
    );
    const previousYearBalance = previousYearEntries - previousYearExits;
    const totalHistoryEntries = sortedHistory.reduce(
      (acc, item) => acc + getPeriodEntries(item),
      0,
    );
    const totalHistoryExits = sortedHistory.reduce(
      (acc, item) => acc + getPeriodExits(item),
      0,
    );
    const totalHistoryBalance = totalHistoryEntries - totalHistoryExits;
    const historyPeriodLabel =
      sortedHistory.length > 0
        ? `${formatPeriodName(sortedHistory[0].year, sortedHistory[0].month)} - ${formatPeriodName(
            sortedHistory[sortedHistory.length - 1].year,
            sortedHistory[sortedHistory.length - 1].month,
          )}`
        : "Sin histórico disponible";

    return [
      {
        id: "totalHistory",
        icon: "bi-clock-history",
        title: "Histórico total",
        periodLabel: historyPeriodLabel,
        entriesLabel: "Entradas",
        exitsLabel: "Salidas",
        balanceLabel: "Balance",
        entries: sortedHistory.length ? totalHistoryEntries : null,
        exits: sortedHistory.length ? totalHistoryExits : null,
        balance: sortedHistory.length ? totalHistoryBalance : null,
        status: sortedHistory.length ? null : "Sin datos históricos",
        trend: { label: "Sin comparativa", direction: "neutral" },
      },
      {
        id: "currentYear",
        icon: "bi-calendar3",
        title: "Año actual",
        periodLabel: String(currentYear),
        entriesLabel: "Entradas",
        exitsLabel: "Salidas",
        balanceLabel: "Balance",
        entries: currentYearHistory.length ? currentYearEntries : null,
        exits: currentYearHistory.length ? currentYearExits : null,
        balance: currentYearHistory.length ? currentYearBalance : null,
        status: currentYearHistory.length ? null : "Sin datos del año actual",
        trend: currentYearHistory.length
          ? getBalanceTrend(
              currentYearBalance,
              previousYearBalance,
              "año anterior",
            )
          : { label: "Sin comparativa", direction: "neutral" },
      },
      buildMonthlyKpi({
        id: "currentMonth",
        icon: "bi-calendar-check",
        title: "Mes actual",
        year: currentYear,
        month: currentMonth,
        emptyMessage: "Sin datos del mes actual",
      }),
      buildMonthlyKpi({
        id: "previousMonth",
        icon: "bi-calendar-minus",
        title: "Mes anterior",
        year: previousCalendarMonth.year,
        month: previousCalendarMonth.month,
        emptyMessage: "Sin datos del mes anterior",
      }),
    ];
  }, [sortedHistory]);

  const kpis = [
    {
      icon: "bi-people-fill",
      tone: "cyan",
      title: "TOTAL PROVEEDORES",
      value: formatInteger(supplierKpis.totalSuppliers),
      subtitle: "Registrados",
      trend: null,
    },
    {
      icon: "bi-receipt-cutoff",
      tone: "amber",
      title: "LÍNEAS DE COMPRA",
      value: formatInteger(
        supplierKpis.purchaseLines || partKpis.purchaseLines,
      ),
      subtitle: "Acumuladas",
      trend: purchaseLinesTrend,
    },
    {
      icon: "bi-gear-fill",
      tone: "violet",
      title: "REPUESTOS ACUMULADOS",
      value: formatInteger(partKpis.totalParts || supplierKpis.distinctParts),
      subtitle: "Unidades",
      trend: accumulatedPartsTrend,
    },
    {
      icon: "bi-trophy-fill",
      tone: "navy",
      title: "PROVEEDOR PRINCIPAL",
      value: topSupplier?.supplier || "-",
      subtitle: "Por gasto total",
      trend: null,
    },
  ];

  const quickSummary = [
    ["Meses analizados", formatInteger(sortedHistory.length)],
    ["Gasto proveedores", formatCurrency(supplierKpis.totalSpend)],
    ["Gasto repuestos", formatCurrency(partKpis.totalSpend)],
  ];

  return (
    <div className="dashboard-page">
      <div className="dashboard-shell">
        <header className="dashboard-hero">
          <div className="dashboard-hero-copy">
            <span className="dashboard-kicker">DASHBOARD</span>
            <h1>DASHBOARD</h1>
            <p className="dashboard-period-label">Resumen económico</p>
            <p className="dashboard-latest-period">
              {formatLatestDataPeriod(latestPeriod?.year, latestPeriod?.month)}
            </p>
          </div>
          <div className="dashboard-hero-actions">
            <button
              type="button"
              className="dashboard-export-btn"
              onClick={handleExport}
              disabled={store.dashboardExportLoading}
            >
              <i className="bi bi-download" aria-hidden="true"></i>
              {store.dashboardExportLoading ? "Exportando..." : "Exportar"}
            </button>
            <label className="dashboard-period-select">
              <span>Periodo</span>
              <select
                value={period}
                onChange={(event) => setPeriod(event.target.value)}
              >
                <option value="12m">Últimos 12 meses</option>
                <option value="6m">Últimos 6 meses</option>
              </select>
            </label>
          </div>
        </header>

        {store.dashboardExportError && (
          <div className="dashboard-export-error" role="alert">
            {store.dashboardExportError}
          </div>
        )}

        <section
          className="dashboard-period-kpis"
          aria-label="Indicadores económicos por periodo"
        >
          {periodKpis.map((periodKpi) => (
            <article
              className={`dashboard-period-card dashboard-period-card-${periodKpi.id}`}
              key={periodKpi.id}
            >
              <div className="dashboard-period-card-head">
                <span className="dashboard-period-icon">
                  <i className={`bi ${periodKpi.icon}`} aria-hidden="true"></i>
                </span>
                <div>
                  <h2>{periodKpi.title}</h2>
                  <p>{periodKpi.periodLabel}</p>
                </div>
                {periodKpi.status && (
                  <span className="dashboard-period-status">
                    {periodKpi.status}
                  </span>
                )}
              </div>
              <div className="dashboard-period-metrics">
                <span className="dashboard-period-metric dashboard-period-metric-entry">
                  <small>Entradas</small>
                  <strong>{formatOptionalCurrency(periodKpi.entries)}</strong>
                </span>
                <span className="dashboard-period-metric dashboard-period-metric-exit">
                  <small>Salidas</small>
                  <strong>{formatOptionalCurrency(periodKpi.exits)}</strong>
                </span>
                <span className="dashboard-period-metric dashboard-period-metric-balance">
                  <small>Balance</small>
                  <strong>{formatOptionalCurrency(periodKpi.balance)}</strong>
                </span>
                <span
                  className={`dashboard-period-metric dashboard-period-metric-trend dashboard-kpi-trend-${periodKpi.trend.direction}`}
                >
                  <small>Variación</small>
                  <strong>
                    {periodKpi.trend.direction === "up" && (
                      <i
                        className="bi bi-arrow-up-short"
                        aria-hidden="true"
                      ></i>
                    )}
                    {periodKpi.trend.direction === "down" && (
                      <i
                        className="bi bi-arrow-down-short"
                        aria-hidden="true"
                      ></i>
                    )}
                    {periodKpi.trend.label}
                  </strong>
                </span>
              </div>
            </article>
          ))}
        </section>

        <section className="dashboard-main-grid">
          <article className="dashboard-card dashboard-chart-card">
            <div className="dashboard-section-head">
              <div>
                <span className="dashboard-section-eyebrow">
                  EVOLUCIÓN ECONÓMICA
                </span>
                <h2>Entradas y salidas por mes</h2>
              </div>
            </div>
            <div className="dashboard-chart-wrap">
              {chartData.length === 0 ? (
                <div className="dashboard-empty-state">
                  No hay datos disponibles
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} />
                    <YAxis
                      width={96}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatAxisCurrency}
                    />
                    <Tooltip content={<DashboardTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar
                      dataKey="entries"
                      fill="#16a34a"
                      name="Entradas"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="exits"
                      fill="#dc2626"
                      name="Salidas"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </article>
        </section>

        <section
          className="dashboard-kpi-grid"
          aria-label="Indicadores operativos"
        >
          {kpis.map((kpi) => (
            <article className="dashboard-kpi-card" key={kpi.title}>
              <span
                className={`dashboard-kpi-icon dashboard-kpi-icon-${kpi.tone}`}
              >
                <i className={`bi ${kpi.icon}`} aria-hidden="true"></i>
              </span>
              <div className="dashboard-kpi-body">
                <span className="dashboard-kpi-title">{kpi.title}</span>
                <strong>{kpi.value}</strong>
                <span className="dashboard-kpi-subtitle">{kpi.subtitle}</span>
                {kpi.trend && (
                  <span
                    className={`dashboard-kpi-trend dashboard-kpi-trend-${kpi.trend.direction}`}
                  >
                    {kpi.trend.direction === "up" && (
                      <i
                        className="bi bi-arrow-up-short"
                        aria-hidden="true"
                      ></i>
                    )}
                    {kpi.trend.direction === "down" && (
                      <i
                        className="bi bi-arrow-down-short"
                        aria-hidden="true"
                      ></i>
                    )}
                    {kpi.trend.label}
                  </span>
                )}
              </div>
            </article>
          ))}
        </section>

        <section className="dashboard-two-col">
          <RankingCard
            title="Ranking por gasto - Proveedores"
            rows={topSuppliers}
            columns={["Proveedor", "Gasto", "Líneas"]}
            empty="No hay proveedores disponibles."
            getKey={(row) => row.supplier_id || row.supplier}
            onDetail={(row) => openSupplier(row.supplier_id)}
            renderRow={(row) => [
              row.supplier || "-",
              formatCurrency(row.total_spend),
              formatInteger(row.purchase_lines),
            ]}
          />
          <RankingCard
            title="Ranking por gasto - Repuestos"
            rows={topParts}
            columns={[
              "Material",
              "Descripción",
              "Gasto total",
              "Cantidad total",
              "Líneas de compra",
              "Proveedores",
              "Precio medio",
            ]}
            empty="No hay repuestos disponibles."
            getKey={(row) => row.material_id}
            onDetail={(row) => openPart(row.material_id)}
            renderRow={(row) => [
              row.material_id || "-",
              {
                content: (
                  <span className="dashboard-ranking-description">
                    {getPartDescription(row)}
                  </span>
                ),
                title: getPartDescription(row),
              },
              formatCurrency(row.total_spend),
              formatOptionalInteger(row.total_quantity),
              formatInteger(row.purchase_lines),
              getSupplierCount(row) === null ||
              getSupplierCount(row) === undefined
                ? "-"
                : formatInteger(getSupplierCount(row)),
              formatOptionalCurrency(getAverageUnitPrice(row)),
            ]}
          />
        </section>

        <section className="dashboard-two-col dashboard-mini-row">
          <MiniSummary
            icon="bi-people-fill"
            title="Catálogo de proveedores"
            buttonText="Ver catálogo"
            onClick={() => navigate("/suppliers-catalog")}
            items={[
              [
                "Total proveedores",
                formatInteger(supplierKpis.totalSuppliers),
                "Base activa",
              ],
              [
                "Gasto total",
                formatCurrency(supplierKpis.totalSpend),
                "Compras",
              ],
              [
                "Repuestos",
                formatInteger(supplierKpis.distinctParts),
                "Distintos",
              ],
            ]}
          />
          <MiniSummary
            icon="bi-gear-fill"
            title="Catálogo de repuestos"
            buttonText="Ver catálogo"
            onClick={() => navigate("/parts-catalog")}
            items={[
              [
                "Total repuestos",
                formatInteger(partKpis.totalParts),
                "Base activa",
              ],
              ["Gasto total", formatCurrency(partKpis.totalSpend), "Compras"],
              [
                "Proveedores",
                formatInteger(partKpis.supplierCount),
                "Acumulados",
              ],
            ]}
          />
        </section>

        <section className="dashboard-bottom-grid">
          <DashboardPanel title="Resumen rápido" icon="bi-lightning-charge">
            <div className="dashboard-summary-list">
              {quickSummary.map(([label, value]) => (
                <span key={label}>
                  <small>{label}</small>
                  <strong>{value}</strong>
                </span>
              ))}
            </div>
          </DashboardPanel>
          <DashboardPanel title="Actividades recientes" icon="bi-clock-history">
            <ul className="dashboard-activity-list">
              <li>Importación de almacén revisada</li>
              <li>Catálogo sincronizado</li>
              <li>KPIs actualizados</li>
            </ul>
          </DashboardPanel>
          <DashboardPanel title="Alertas y notificaciones" icon="bi-bell">
            <ul className="dashboard-alert-list">
              <li>Revisar proveedores sin detalle asociado</li>
              <li>Validar últimos movimientos económicos</li>
              <li>Control de salidas pendiente</li>
            </ul>
          </DashboardPanel>
          {isAdmin && (
            <DashboardPanel title="Accesos rápidos" icon="bi-grid-3x3-gap">
              <div className="dashboard-quick-actions">
                <button
                  type="button"
                  onClick={() => navigate("/warehouse-cost-analytics")}
                >
                  Coste almacén
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/imports/warehouse-excel")}
                >
                  Importar
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/add-inventory-data")}
                >
                  Nuevo mes
                </button>
              </div>
            </DashboardPanel>
          )}
        </section>
      </div>
    </div>
  );
};

const RankingCard = ({
  title,
  rows,
  columns,
  empty,
  getKey,
  onDetail,
  renderRow,
}) => (
  <article className="dashboard-card dashboard-ranking-card">
    <div className="dashboard-section-head">
      <div>
        <span className="dashboard-section-eyebrow">Top 3</span>
        <h2>{title}</h2>
      </div>
    </div>
    <div className="table-responsive">
      <table className="table table-compact mb-0 dashboard-ranking-table">
        <thead>
          <tr>
            <th>#</th>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td className="table-empty" colSpan={columns.length + 1}>
                {empty}
              </td>
            </tr>
          )}
          {rows.map((row, index) => {
            const values = renderRow(row);
            return (
              <tr
                key={getKey(row) || index}
                className="dashboard-ranking-clickable-row"
                onClick={() => onDetail(row)}
                role="link"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onDetail(row);
                  }
                }}
              >
                <td>
                  <span
                    className={`dashboard-rank-badge dashboard-rank-badge-${index + 1}`}
                  >
                    {index + 1}
                  </span>
                </td>
                {values.map((value, valueIndex) => {
                  const cell =
                    value && typeof value === "object" && "content" in value
                      ? value
                      : null;
                  return (
                    <td
                      key={`${getKey(row) || index}-${valueIndex}`}
                      title={cell?.title}
                    >
                      {cell ? cell.content : value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </article>
);

const MiniSummary = ({ icon, title, buttonText, onClick, items }) => (
  <article className="dashboard-card dashboard-mini-summary">
    <div className="dashboard-mini-head">
      <span className="dashboard-mini-icon">
        <i className={`bi ${icon}`} aria-hidden="true"></i>
      </span>
      <div>
        <h2>{title}</h2>
        <p>Indicadores compactos y tendencias principales.</p>
      </div>
      <button type="button" onClick={onClick}>
        {buttonText}
      </button>
    </div>
    <div className="dashboard-mini-kpis">
      {items.map(([label, value, trend]) => (
        <span key={label}>
          <small>{label}</small>
          <strong>{value}</strong>
          <em>{trend}</em>
        </span>
      ))}
    </div>
  </article>
);

const DashboardPanel = ({ title, icon, children }) => (
  <article className="dashboard-card dashboard-bottom-panel">
    <div className="dashboard-bottom-head">
      <i className={`bi ${icon}`} aria-hidden="true"></i>
      <h2>{title}</h2>
    </div>
    {children}
  </article>
);

export default Dashboard;
